import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Settings, PenTool } from './icons';
import { OfflineIndicator } from '../shared/components/OfflineIndicator';
import { useAuthStore } from '../shared/stores/authStore';

/* ── View mode: reading (plaza) vs creative (workshop/studio) ── */
export type ViewMode = 'reading' | 'creative';

/* ── User dropdown menu ─────────────────────────────────────── */
function UserMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const initial = user?.user_metadata?.username?.charAt(0)?.toUpperCase()
    ?? user?.email?.charAt(0)?.toUpperCase()
    ?? 'U';

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary text-sm font-semibold hover:bg-primary/25 transition-colors"
      >
        {initial}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-border bg-card shadow-xl py-1 z-50">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-medium truncate">{user?.user_metadata?.username ?? '用户'}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <button
            onClick={async () => { setOpen(false); try { await signOut(); } catch {} navigate('/'); }}
            className="w-full text-left px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
          >
            退出登录
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Mobile hamburger menu ──────────────────────────────────── */
function MobileMenu({ onSwitchView }: { onSwitchView: (v: ViewMode) => void }) {
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();

  return (
    <>
      <button onClick={() => setOpen(!open)} className="flex md:hidden h-9 w-9 items-center justify-center rounded-lg hover:bg-accent transition-colors">
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          {open ? <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></> : <><path d="M4 8h16" /><path d="M4 16h16" /></>}
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full border-b border-border bg-card/98 backdrop-blur-lg md:hidden z-40">
          <div className="p-4 space-y-1">
            <button
              onClick={() => { setOpen(false); onSwitchView('reading'); }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <BookOpen className="h-4.5 w-4.5" />
              阅读广场
            </button>
            <button
              onClick={() => { setOpen(false); onSwitchView('creative'); }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <PenTool className="h-4.5 w-4.5" />
              创作中心
            </button>
          </div>
          <div className="border-t border-border p-4">
            {user ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">{user.user_metadata?.username ?? user.email}</p>
                <button
                  onClick={async () => { setOpen(false); try { await signOut(); } catch {} navigate('/'); }}
                  className="text-sm text-destructive hover:underline"
                >
                  退出登录
                </button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Link to="/auth/login" onClick={() => setOpen(false)}
                  className="flex-1 rounded-lg border border-border py-2 text-sm font-medium hover:bg-accent transition-colors text-center">
                  登录
                </Link>
                <Link to="/auth/register" onClick={() => setOpen(false)}
                  className="flex-1 rounded-lg bg-primary py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors text-center">
                  注册
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ── Floating settings button (bottom-right) ────────────────── */
function FloatingSettingsButton({ onClick }: { onClick: () => void }) {
  return (
    <motion.button
      onClick={onClick}
      className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-white border border-border shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-brand-500/15 transition-shadow group"
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.95 }}
      title="通用设置"
    >
      <Settings className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
    </motion.button>
  );
}

/* ── Settings drawer (slides in from right) ─────────────────── */
function SettingsDrawer({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          {/* Drawer */}
          <motion.div
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-card border-l border-border shadow-2xl overflow-auto"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 backdrop-blur-lg px-6 py-4">
              <h2 className="font-serif text-lg font-bold">通用设置</h2>
              <button
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-accent transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ── Creative side tab (prominent vertical banner on right edge) ─────────── */
function CreativeTab({ onClick }: { onClick: () => void; isActive: boolean }) {
  return (
    <>
      {/* Outer glow halo (decorative, outside button) */}
      <div className="hidden md:block fixed right-0 top-1/2 -translate-y-1/2 z-[19] w-28 h-72 rounded-l-full bg-brand-500/10 blur-3xl pointer-events-none" />

      <motion.button
        onClick={onClick}
        className="
          hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-20
          flex-col items-center justify-center gap-5
          w-20 h-[280px]
          rounded-l-[32px]
          bg-gradient-to-b from-brand-400 via-brand-600 to-brand-800
          text-white cursor-pointer
          shadow-[0_0_60px_rgba(124,58,237,0.4)]
          hover:shadow-[0_0_80px_rgba(124,58,237,0.55)]
          hover:w-[88px]
          transition-all duration-300 ease-out
          group overflow-hidden
          border-l-2 border-t-2 border-b-2 border-white/10
        "
        initial={{ x: 40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 240, damping: 22 }}
        whileHover={{ x: -6 }}
        whileTap={{ scale: 0.97 }}
      >
        {/* Shimmer sweep */}
        <motion.div
          className="absolute inset-0 rounded-l-[32px]"
          style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)' }}
          animate={{ y: ['-100%', '100%'] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1 }}
        />

        {/* Icon container */}
        <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300 shadow-lg shadow-black/10">
          <PenTool className="h-7 w-7 drop-shadow-md" />
        </div>

        {/* Main vertical text */}
        <span className="relative text-xl font-black [writing-mode:vertical-lr] tracking-[0.4em] drop-shadow-lg">
          创作
        </span>

        {/* Sub label */}
        <span className="relative text-[10px] font-semibold tracking-widest opacity-60 uppercase">
          Studio
        </span>

        {/* Animated chevron */}
        <motion.svg
          className="relative h-5 w-5 opacity-70"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          animate={{ x: [0, -5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <path d="M15 18l-6-6 6-6" />
        </motion.svg>
      </motion.button>
    </>
  );
}

/* ── "Back to reading" tab (on left edge when in creative mode) ── */
function ReadingTab({ onClick }: { onClick: () => void }) {
  return (
    <motion.button
      onClick={onClick}
      className="hidden md:flex fixed left-0 top-1/2 -translate-y-1/2 z-20 flex-col items-center gap-2 px-2.5 py-6 rounded-r-2xl border border-l-0 border-border bg-white/95 backdrop-blur-lg text-muted-foreground hover:text-primary hover:bg-brand-50 hover:border-brand-200 shadow-lg shadow-black/5 transition-colors"
      whileHover={{ x: 4 }}
      whileTap={{ scale: 0.97 }}
    >
      <BookOpen className="h-5 w-5" />
      <span className="text-xs font-semibold [writing-mode:vertical-lr] tracking-wider">阅 读</span>
    </motion.button>
  );
}

/* ── Page-flip transition variants ──────────────────────────── */
const pageVariants = {
  enterFromRight: {
    initial: { x: '100%', opacity: 0, rotateY: -15 },
    animate: { x: 0, opacity: 1, rotateY: 0 },
    exit: { x: '-30%', opacity: 0, rotateY: 10 },
  },
  enterFromLeft: {
    initial: { x: '-100%', opacity: 0, rotateY: 15 },
    animate: { x: 0, opacity: 1, rotateY: 0 },
    exit: { x: '30%', opacity: 0, rotateY: -10 },
  },
};

const pageTransition = {
  type: 'spring' as const,
  damping: 28,
  stiffness: 260,
  mass: 0.8,
};

/* ── Root Layout ────────────────────────────────────────────── */
export function RootLayout() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Derive view mode from current route
  const viewMode: ViewMode = location.pathname.startsWith('/workshop') || location.pathname.startsWith('/studio')
    ? 'creative'
    : 'reading';

  const switchToCreative = useCallback(() => {
    navigate('/workshop');
  }, [navigate]);

  const switchToReading = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Direction for animation
  const direction = viewMode === 'creative' ? 'enterFromRight' : 'enterFromLeft';

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground" style={{ perspective: '1200px' }}>
      {/* ── Top Navigation Bar ────────────────────────────── */}
      <header className="relative z-30 flex h-14 shrink-0 items-center border-b border-border bg-white/80 backdrop-blur-xl px-4 md:px-6">
        {/* Left: Logo */}
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-brand-500/20">
              N
            </div>
            <span className="hidden sm:block text-base font-bold tracking-tight font-serif">诺文</span>
          </Link>
        </div>

        {/* Right: Actions */}
        <div className="ml-auto flex items-center gap-3">
          {user ? (
            <>
              <UserMenu />
            </>
          ) : (
            <>
              <Link to="/auth/login"
                className="hidden md:block rounded-lg px-3.5 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                登录
              </Link>
              <Link to="/auth/register"
                className="hidden md:block rounded-lg bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
                免费注册
              </Link>
            </>
          )}

          {/* Mobile menu toggle */}
          <MobileMenu onSwitchView={(v) => v === 'creative' ? switchToCreative() : switchToReading()} />
        </div>
      </header>

      {/* ── Main Content with page-flip transition ────────── */}
      <main className="relative flex-1 overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={viewMode}
            className="absolute inset-0 overflow-auto scroll-smooth"
            initial={pageVariants[direction].initial}
            animate={pageVariants[direction].animate}
            exit={pageVariants[direction].exit}
            transition={pageTransition}
            style={{ transformOrigin: viewMode === 'creative' ? 'left center' : 'right center' }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* ── Side tabs (edge navigation) ───────────────────── */}
      {viewMode === 'reading' && (
        <CreativeTab onClick={switchToCreative} isActive={false} />
      )}
      {viewMode === 'creative' && (
        <ReadingTab onClick={switchToReading} />
      )}

      {/* ── Floating settings button (bottom-right) ───────── */}
      <FloatingSettingsButton onClick={() => setSettingsOpen(true)} />

      {/* ── Settings drawer ───────────────────────────────── */}
      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <SettingsContent />
      </SettingsDrawer>

      {/* ── Offline Indicator ────────────────────────────── */}
      <OfflineIndicator />
    </div>
  );
}

/* ── Inline Settings Content (reused from SettingsPage) ─────── */
function SettingsContent() {
  return (
    <div className="space-y-6">
      {/* Theme placeholder */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3">🎨 外观</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium">主题模式</p>
              <p className="text-xs text-muted-foreground">选择浅色或深色主题</p>
            </div>
            <div className="flex gap-1 rounded-lg bg-muted p-1">
              <button className="rounded-md bg-background px-3 py-1 text-xs font-medium shadow-sm">浅色</button>
              <button className="rounded-md px-3 py-1 text-xs font-medium text-muted-foreground">深色</button>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium">字体大小</p>
              <p className="text-xs text-muted-foreground">调整全局字体大小</p>
            </div>
            <div className="flex items-center gap-2">
              <button className="flex h-7 w-7 items-center justify-center rounded border border-border text-sm hover:bg-accent">A-</button>
              <span className="text-sm font-medium w-8 text-center">14</span>
              <button className="flex h-7 w-7 items-center justify-center rounded border border-border text-sm hover:bg-accent">A+</button>
            </div>
          </div>
        </div>
      </section>

      {/* Reading settings */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3">📖 阅读</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium">阅读模式</p>
              <p className="text-xs text-muted-foreground">单栏或双栏阅读</p>
            </div>
            <div className="flex gap-1 rounded-lg bg-muted p-1">
              <button className="rounded-md bg-background px-3 py-1 text-xs font-medium shadow-sm">单栏</button>
              <button className="rounded-md px-3 py-1 text-xs font-medium text-muted-foreground">双栏</button>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section>
        <h3 className="text-sm font-semibold text-foreground mb-3">ℹ️ 关于</h3>
        <div className="rounded-lg border border-border p-3 text-sm text-muted-foreground">
          <p><strong>NovelStudio</strong> v0.1.0</p>
          <p className="mt-1">AI辅助网文创作平台</p>
          <p className="mt-2 text-xs">© 2026 诺文 NovelStudio · AI-Powered Writing Studio</p>
        </div>
      </section>
    </div>
  );
}
